<script setup lang="ts">
import { nextTick, ref } from 'vue';
import { dateHelpers, QuestionEmail, QuestionPhone, ContactPhone, LocationSelector } from '@hfc/ui-core';
import { useForm, Field, ErrorMessage } from 'vee-validate';
import QuestionButtonSet from '@/application/shared/infrastructure/components/questionTypes/QuestionButtonSet.vue';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import AnimatedConditionalQuestion from '@/application/shared/infrastructure/components/questionTypes/AnimatedConditionalQuestion.vue';
import { CONTACT_PREFERENCE_TYPES } from '@hfc/common/src/types/ContactPreferenceTypes';
import { useUrlHelper } from '@/shared/infrastructure/composition/useUrlHelper';
import DateSelector from '@/shared/infrastructure/components/DateSelector.vue';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { useCheckCoverage } from '@/quote/infrastructure/composition/useCheckCoverage';
import { useMedicareConsentStore } from '@/consent/medicare/infrastructure/composition/useMedicareConsentStore';

defineProps<{
  showContactPreference: () => boolean;
  title: string;
}>();

const { medicareConsentStore, medicareConsentState } = useMedicareConsentStore();
const { checkCoverage } = useCheckCoverage();

const { handleSubmit, validate } = useForm();
const { getBaseUrl } = useUrlHelper();
const runValidations = handleSubmit(() => true);
const { getDateFromAge } = dateHelpers();

const agreeToReceiveText = ref(medicareConsentState.value.application.agree_to_receive_text);

const contactPreferences = [{
  text: 'Text',
  value: CONTACT_PREFERENCE_TYPES.TEXT
}, {
  text: 'Email',
  value: CONTACT_PREFERENCE_TYPES.EMAIL
}];

const phoneRef = ref<{ handleSubmit: () => Promise<boolean> } | null>(null);
const locationSelectorRef = ref<{ isValidLocation: () => Promise<boolean> } | null>(null);

const firstName = ref(medicareConsentState.value.application.contactInfo.firstName);
const lastName = ref(medicareConsentState.value.application.contactInfo.lastName);
const email = ref(medicareConsentState.value.application.contactInfo.email);
const phone = ref(medicareConsentState.value.application.contactInfo.phone);
const dob = ref(medicareConsentState.value.application.contactInfo.dateOfBirth);
const contactPreference = ref<CONTACT_PREFERENCE_TYPES>(medicareConsentState.value.application.contact_preference as CONTACT_PREFERENCE_TYPES);

const updateAgreeToReceiveText = async (): Promise<void> => {
  await nextTick();
  medicareConsentStore.updateForm({ agree_to_receive_text: agreeToReceiveText.value });
};

const updateContactInfo = (field: string, value: string | number | undefined): void => {
  medicareConsentStore.updateContactInfo({ [field]: value });
};

const contactPreferenceUpdated = (value: CONTACT_PREFERENCE_TYPES): void => {
  if (value !== CONTACT_PREFERENCE_TYPES.TEXT) {
    agreeToReceiveText.value = false;
    updateAgreeToReceiveText();
  }
  contactPreference.value = value;
  medicareConsentStore.updateForm({ contact_preference: value });
};

const sectionSubmit = async (): Promise<boolean | undefined> => {
  const localValid = await runValidations();
  const phoneValid = await phoneRef.value?.handleSubmit();
  const locationValid = await locationSelectorRef.value?.isValidLocation();

  return localValid && phoneValid && locationValid;
};

defineExpose({
  sectionSubmit: sectionSubmit,
  sectionIsValid: async (): Promise<boolean> => {
    const result = await validate({ mode: 'silent' });

    return result.valid;
  }
});

</script>
<template>
  <BaseSection :title="title">
    <div class="flex flex-col">
      <div class="flex flex-row gap-4 mb-4">
        <div class="flex flex-col w-full">
          <label class="font-medium mb-1 whitespace-nowrap text-sky-700 block">
            First Name <span class="font-serif">*</span>
          </label>
          <Field v-model="firstName" type="text" v-slot="{ field, errorMessage, meta }" name="medicare-first-name" rules="required|min:2|max:30">
            <input v-capitalized="field" v-only-letters="'.-'" v-bind="field" :class="{ ...meta }" @change="() => updateContactInfo('firstName', firstName.trim())">
            <span class="error">{{ errorMessage }}</span>
          </Field>
        </div>
        <div class="flex flex-col w-full">
          <label class="font-medium mb-1 whitespace-nowrap text-sky-700 block">
            Last Name <span class="font-serif">*</span>
          </label>
          <Field v-model="lastName" type="text" v-slot="{ field, errorMessage, meta }" name="medicare-last-name" rules="required|min:2|max:30">
            <input v-capitalized="field" v-only-letters="'.-'" v-bind="field" :class="{ ...meta }" @change="() => updateContactInfo('lastName', lastName.trim())">
            <span class="error">{{ errorMessage }}</span>
          </Field>
        </div>
      </div>
      <div class="flex flex-row gap-4 mb-4">
        <div class="flex flex-col w-full">
          <label class="font-medium mb-1 whitespace-nowrap text-sky-700 block">
            Date of Birth <span class="font-serif">*</span>
          </label>
          <Field v-model="dob" v-slot="{ field, errorMessage, meta }" :name="`medicare-dob`" :rules="{ required: true, not_one_of: [0], min_date: getDateFromAge(18), max_date: getDateFromAge(200) }">
            <DateSelector label="" size="h-14" v-bind:model-value="field.value" :has-errors="!meta.valid && meta.validated" :is-valid="meta.valid && meta.validated" id="medicare-dob" ref="dobRef" name="medicare-dob" :max-date="new Date()" :disable-placeholder="true" @update:model-value="(v: number) => { dob = v; updateContactInfo('dateOfBirth', dob); }" />
            <span v-if="errorMessage" class="error">{{errorMessage}}</span>
          </Field>
        </div>
        <div class="flex flex-col w-full">
          <LocationSelector
            class="gap-4"
            ref="locationSelectorRef"
            placeholder=""
            :id="'medicare-location'"
            :hide-labels="false"
            :zipcode="medicareConsentState.application.contactInfo.zipCode"
            :county="medicareConsentState.application.contactInfo.county"
            :show-required-on-label="true"
            :application-type="APPLICATION_TYPES.MEDICARE"
            :check-coverage="checkCoverage"
            :vertical-mode="false"
            zip-size="w-full"
            county-size="w-full"
            @update:zip-code="updateContactInfo('zipCode', $event)"
            @update:county="updateContactInfo('county', $event)" />
        </div>
      </div>
      <div class="flex flex-row gap-4 mb-4">
        <div class="flex flex-col w-full">
          <label class="font-medium mb-1 whitespace-nowrap text-sky-700 block">
            Email <span class="font-serif">*</span>
          </label>
          <QuestionEmail :use-form-context="true" :value="email" :disable-placeholder="true" @update:value="(v: string) => { email = v; updateContactInfo('email', email); }" id="medicare-email" name="medicare-email" />
        </div>
        <div class="flex flex-col w-full">
          <label class="font-medium mb-1 whitespace-nowrap text-sky-700 block">
            Phone <span class="font-serif">*</span>
          </label>
          <QuestionPhone ref="phoneRef" :value="{ number: phone }" phone-input-placeholder="" @update:value="(v: ContactPhone) => { phone = v.number; updateContactInfo('phone', phone); }" id="medicare-phone" name="medicare-phone" :hide-ext="true" :hide-type="true" />
        </div>
      </div>
    </div>
    <div v-if="showContactPreference()" class="mt-6 mb-4">
      <Field v-model="contactPreference" type="text" v-slot="{ field, errorMessage }" name="medicare-contact-preference" rules="required">
        <QuestionButtonSet :options="contactPreferences" v-bind:model-value="field.value" @update:model-value="val => contactPreferenceUpdated(val as CONTACT_PREFERENCE_TYPES)" id="contactPreference" name="contactPreference" question-text="How would you like to receive your appointment confirmation?" />
        <div class="flex h-5 sm:h-7">
          <div class="sm:flex-1"></div>
          <div v-if="errorMessage" class="error w-full sm:w-auto mt-1 md:w-80">
            {{ errorMessage }}
          </div>
        </div>
      </Field>
    </div>
    <AnimatedConditionalQuestion :show="contactPreference === CONTACT_PREFERENCE_TYPES.TEXT">
      <Field :value="true" v-slot="{ field }" name="agreeToReceiveText" type="checkbox" rules="required" v-model="agreeToReceiveText">
        <div class="flex flex-row items-start">
          <input type="checkbox" class="h-4 w-4 transition duration-150 ease-in-out mt-2" id="agreeToReceiveText" v-bind="field" @change="updateAgreeToReceiveText" />
          <label for="agreeToReceiveText" class="font-medium text-md ml-2 max-w-[calc(100%-40px)]">
            <small>
              By selecting Text, you agree to receive automated appointment confirmation and reminder messages from Health for California Insurance Center. Message and data rates may apply. Reply STOP to unsubscribe. View our <a :href="`${getBaseUrl('/privacy-policy')}`" target="_blank">Privacy Policy</a> and <a :href="`${getBaseUrl('/account-terms-and-conditions')}`" target="_blank">Terms of Service</a>.
            </small>
          </label>
        </div>
      </Field>
      <ErrorMessage name="agreeToReceiveText" class="error" />
    </AnimatedConditionalQuestion>
  </BaseSection>
</template>
